import * as React from "react";
import { createPortal } from "react-dom";
import PubSub from "pubsub-js";

import { withDefault } from "collection-utils";

import {
    NavBarStyle,
    NavBarButtonWrapper,
    BackText,
    TitleStyle,
    RightButtonsWrapper,
    ModalNavOuter,
    ModalNavWrapper,
    LeftButtonsWrapper,
    RightButtonsInner,
    ShadowItem,
    MarginSpacer,
    NavBarFabWrapper,
} from "./nav-bar-style";
import { withTheme } from "styled-components";
import { RuntimeTheme } from "../../../lib/theme/theme";
import { MenuItem } from "../../../lib/menu-item";
import { nonNull, defined } from "common/Support";
import FloatingButton from "../../../ui/views/floating-button/floating-button";
import { TITLE_BAR_TAP, APP_MODAL_ROOT } from "../../../lib/constants";
import { UI, RenderEnvironment, Context } from "common/components/types";
import { getLocalizedString } from "common/components/localization";
import { NavBarButton } from "./nav-bar-button";
import { renderEmptyAsUndefined } from "common/components/uis";
import sortBy from "lodash/sortBy";
import AppIcon from "common/app-icon";
import SearchBar from "../search-bar/search-bar";
import { supportsOfflineActionQueue } from "common/offline-action-queue";

const androidBackGlyph = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M16 7H3.8L9.4 1.4L8 0L0 8L8 16L9.4 14.6L3.8 9H16V7Z" />
    </svg>
);

const iOSBackGlyph = (
    <svg width="13" height="21" viewBox="0 0 13 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.75946 0.412269L0.395654 9.40223C-0.131885 9.90796 -0.131885 10.7522 0.395654 11.26L9.75946 20.2479C10.3337 20.7993 11.2403 20.7993 11.8146 20.2479C12.4221 19.6643 12.4221 18.6923 11.8146 18.1077L3.97212 10.5788C3.82985 10.4428 3.82985 10.2174 3.97212 10.0814L11.8146 2.55254C12.4221 1.96892 12.4221 0.996923 11.8146 0.412269C11.5269 0.138115 11.1572 0 10.7875 0C10.4168 0 10.0471 0.138115 9.75946 0.412269Z"
        />
    </svg>
);

interface BaseProps {
    title: string;
    showTitle?: boolean;
    onBack?(): void;
    barButtonItems?: MenuItem[];
    searchBar?: UI;
    ctx?: Context;
    // This allows us to do custom client side filtering
    searchValue?: string;
    onSearchChanged?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchCleared?: () => void;
    renderEnvironment: RenderEnvironment;
}

interface Props extends BaseProps {
    theme?: RuntimeTheme;
}

interface State {
    showSearch: boolean;
}

class NavBar extends React.PureComponent<Props, State> {
    public state: State = { showSearch: false };

    private clearSearch: (() => void) | undefined = undefined;

    private createNavButton(menuItem: MenuItem, index?: number): React.ReactNode {
        const offlineAddRow = supportsOfflineActionQueue(this.props.renderEnvironment.appEnvironment.appFeatures);
        return (
            <NavBarButton
                theme={defined(this.props.theme)}
                menuItem={menuItem}
                key={index}
                offlineAddRow={offlineAddRow}
            />
        );
    }

    private createBackButton(isAndroid: boolean, onBack: () => void): React.ReactNode {
        if (isAndroid) {
            return (
                <NavBarButtonWrapper
                    data-test="back-button"
                    aria-label="back"
                    enabled={true}
                    setFill={true}
                    onClick={onBack}>
                    {androidBackGlyph}
                </NavBarButtonWrapper>
            );
        }
        return (
            <NavBarButtonWrapper
                data-test="back-button"
                aria-label="back"
                enabled={true}
                setFill={true}
                onClick={onBack}>
                {iOSBackGlyph}
                <BackText>{getLocalizedString("back")}</BackText>
            </NavBarButtonWrapper>
        );
    }

    private onTitleClick = () => {
        PubSub.publish(TITLE_BAR_TAP, undefined);
    };

    public render() {
        if (this.props.theme === undefined) {
            return null;
        }
        const { onBack, title, ctx, renderEnvironment, searchValue, onSearchChanged, onSearchCleared } = this.props;
        let barButtonItems = withDefault(this.props.barButtonItems, []);
        let searchBar: React.ReactNode;
        if (this.props.searchBar !== undefined && ctx !== undefined && renderEnvironment !== undefined) {
            searchBar = renderEmptyAsUndefined(
                this.props.searchBar,
                ctx,
                renderEnvironment.with({
                    registerSearchBarClearer: x => (this.clearSearch = x),
                })
            );
        } else if (searchValue !== undefined && onSearchChanged !== undefined && onSearchCleared !== undefined) {
            this.clearSearch = onSearchCleared;
            searchBar = (
                <SearchBar placeholder={getLocalizedString("search")} value={searchValue} onChange={onSearchChanged} />
            );
        }

        if (searchBar !== undefined && this.state.showSearch) {
            const closeBtn = [
                this.createNavButton(
                    {
                        title: "Exit",
                        icon: "00-01-glide-close",
                        onClick: () => {
                            if (this.clearSearch !== undefined) {
                                this.clearSearch();
                            }
                            this.setState({ showSearch: false });
                        },
                    },
                    99
                ),
            ];
            return (
                <NavBarStyle className="nav-bar-root">
                    {searchBar}
                    <RightButtonsWrapper>{closeBtn}</RightButtonsWrapper>
                </NavBarStyle>
            );
        }

        const acceptButton = barButtonItems.find(i => i.style === "platform-accept");
        const cancelButton = barButtonItems.find(i => i.style === "platform-cancel");
        const submitButton = barButtonItems.find(i => i.style === "platform-submit");
        const shareButton = barButtonItems.find(i => i.style === "share");
        const menuButton = barButtonItems.find(i => i.style === "menu");
        barButtonItems = barButtonItems.filter(
            i => i !== acceptButton && i !== cancelButton && i !== shareButton && i !== submitButton && i !== menuButton
        );

        let backButton: React.ReactNode;
        const isAndroid = this.props.theme.navBar.style === "Android";
        const isIOS = !isAndroid;
        if (cancelButton !== undefined) {
            backButton = this.createNavButton(cancelButton);
        } else if (onBack !== undefined) {
            backButton = this.createBackButton(isAndroid, onBack);
        } else if (menuButton !== undefined) {
            backButton = this.createNavButton(menuButton);
        } else if (shareButton !== undefined) {
            backButton = this.createNavButton(shareButton);
        }

        // Cancel handled by this point
        let fab: React.ReactNode;
        const isTablet = renderEnvironment.sizeClass === "tablet";
        if (submitButton !== undefined && isAndroid && !isTablet) {
            fab = createPortal(
                <NavBarFabWrapper>
                    <FloatingButton
                        isEnabled={submitButton.isEnabled !== false}
                        onClick={() =>
                            submitButton.isEnabled !== false && submitButton.onClick && submitButton.onClick(false)
                        }>
                        <AppIcon icon={submitButton.icon} size={24} color={this.props.theme.baseColor} />
                    </FloatingButton>
                </NavBarFabWrapper>,
                nonNull(document.getElementById(APP_MODAL_ROOT))
            );
        } else if (submitButton !== undefined && (isIOS || isTablet)) {
            barButtonItems.push(submitButton);
        }

        if (acceptButton) {
            barButtonItems.push(acceptButton);
        }

        if (searchBar !== undefined) {
            barButtonItems.splice(0, 0, {
                title: "Search",
                icon: "00-01-glide-search",
                onClick: () => {
                    this.setState({ showSearch: true });
                },
            });
        }

        let rightButtons: React.ReactNode;
        if (barButtonItems.length > 0) {
            rightButtons = sortBy(barButtonItems, [mi => mi.purpose])
                .reverse()
                .map((mi, i) => {
                    return this.createNavButton(mi, i);
                });
        }

        let opacity = 1;
        if (this.props.showTitle === false) {
            opacity = 0;
        }

        if (backButton === undefined) {
            backButton = <MarginSpacer />;
        }

        return (
            <NavBarStyle className="nav-bar-root" data-test="nav-bar" role="navigation">
                <LeftButtonsWrapper>
                    {isIOS && (
                        <ShadowItem aria-hidden={true} tabIndex={-1}>
                            <RightButtonsInner>{rightButtons}</RightButtonsInner>
                        </ShadowItem>
                    )}
                    {backButton}
                    {fab}
                </LeftButtonsWrapper>
                <TitleStyle opacity={opacity} onClick={this.onTitleClick} aria-label={title}>
                    {title}
                </TitleStyle>
                <RightButtonsWrapper>
                    {isIOS && (
                        <ShadowItem aria-hidden={true} tabIndex={-1}>
                            {backButton}
                        </ShadowItem>
                    )}
                    <RightButtonsInner>{rightButtons}</RightButtonsInner>
                </RightButtonsWrapper>
            </NavBarStyle>
        );
    }
}

const ThemedNavBar = withTheme(NavBar);

interface ModalNavProps extends BaseProps {
    theme: RuntimeTheme;
    searchValue?: string;
    onSearchChanged?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchCleared?: () => void;
}

class ModalNavBar extends React.PureComponent<ModalNavProps> {
    public render(): React.ReactNode {
        const { theme, searchValue, onSearchChanged, onSearchCleared } = this.props;
        const isAndroid = theme.navBar.style === "Android";
        const showSearch = searchValue !== undefined && onSearchChanged !== undefined && onSearchCleared !== undefined;
        const showSearchInline = showSearch && isAndroid;
        const showSearchBelow = showSearch && !isAndroid;
        return (
            <ModalNavOuter>
                <ModalNavWrapper data-test="modal-nav-bar">
                    <ThemedNavBar
                        {...this.props}
                        searchValue={showSearchInline ? searchValue : undefined}
                        onSearchChanged={showSearchInline ? onSearchChanged : undefined}
                        onSearchCleared={showSearchInline ? onSearchCleared : undefined}
                    />
                </ModalNavWrapper>
                {showSearchBelow && (
                    <SearchBar
                        placeholder={getLocalizedString("search")}
                        value={searchValue}
                        onChange={defined(onSearchChanged)}
                        onClear={onSearchCleared}
                    />
                )}
            </ModalNavOuter>
        );
    }
}

const ThemedModalNavBar = withTheme(ModalNavBar);

export { ThemedModalNavBar };
export default ThemedNavBar;
